import gsap from "gsap"
import GSDevTools from "gsap/GSDevTools"
import type { AnimationI, TickerI } from "tweenly"
import { applyJoinedScrollTexts } from "tweenly"
import { initSubTimelineTween } from "../tween.helpers"
import { BaseTicker } from "./BaseTicker"
import { ScrollCtx } from "./types"
gsap.registerPlugin(GSDevTools)

export class ScrollUpTicker extends BaseTicker {
    private readonly maskObject: TickerI
    private readonly animationGroup: AnimationI[]
    private readonly textListNode: HTMLElement | null
    private readonly scrollTime: number
    private readonly scrollPause: number

    constructor(args: ScrollCtx) {
        super(args.timeline, args.objectId, args.joinedTexts, args.childTexts, args.assets)
        this.maskObject = args.maskObject
        this.animationGroup = args.animationGroup
        this.textListNode = args.textListNode
        this.scrollTime = args.scrollTime
        this.scrollPause = args.scrollPause
    }

    //needed for prevent glitching
    private killTweens() {
        gsap.killTweensOf(`#${this.objectId}`)
        for (const childId of this.maskObject.childIds) {
            gsap.killTweensOf(`#${childId}`)
        }
    }

    protected intro() {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode } = this

        if (textListNode) {
            timeline.set(`#${textListNode.id}`, {
                transform: "translateY(-100%)",
            })
            for (const animation of this.animationGroup) {
                if (animation.subTimelineType?.startsWith("scrollUp")) {
                    initSubTimelineTween(animation, objectId, timeline)
                }
            }

            const oldCopy = document.getElementById(objectId + "-copy")
            if (oldCopy) {
                oldCopy.remove()
            }
        }
        timeline.set(`#${objectId}`, {
            x: `0px`,
            onComplete: () => {
                this.repeat()
            },
        })
    }

    private repeat() {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode } = this

        const numOfTextElements = this.textListNode?.children.length || 1
        const textListHeightPerc = 100 / numOfTextElements

        if (textListNode) {
            timeline.set(`#${textListNode.id}`, {
                transform: "translateY(-100%)",
            })
            for (const animation of this.animationGroup) {
                if (animation.subTimelineType?.startsWith("scrollUp")) {
                    initSubTimelineTween(animation, objectId, timeline)
                }
            }

            const oldCopy = document.getElementById(objectId + "-copy")
            if (oldCopy) {
                oldCopy.remove()
            }
            const parent = textListNode.parentNode
            if (parent) {
                const textList2Node = textListNode.cloneNode(true) as HTMLElement
                textList2Node.id = objectId + "-copy"
                parent.appendChild(textList2Node)

                timeline.fromTo(
                    `#${textList2Node.id}`,
                    { transform: `translateY(${-100 + 1 * textListHeightPerc}%)` },
                    {
                        transform: `translateY(${-100 + 0 * textListHeightPerc}%)`,
                        ease: "none",
                        duration: this.scrollTime,
                    },
                    0
                )

                timeline.set(`#${textList2Node.id}`, {
                    transform: `translateY(${0 + 1 * textListHeightPerc}%)`,
                })
            }
        }

        timeline.set(`#${this.objectId}`, {
            x: "0px",
            onComplete: () => this.handleRepeat(() => this.repeat()),
        })
    }

    protected outro(newTexts?: string) {
        this.killTweens()
        this.timeline.clear()
        const { timeline, objectId, textListNode, scrollTime, scrollPause } = this

        const numOfTextElements = this.textListNode?.children.length || 1
        const textListHeightPerc = 100 / numOfTextElements

        const delayOfLastText =
            numOfTextElements * (scrollTime ? scrollTime : 0) +
            numOfTextElements * (scrollPause ? scrollTime : 0)

        if (textListNode) {
            timeline.set(`#${textListNode.id}`, {
                transform: "translateY(-100%)",
            })
            timeline.fromTo(
                `#${textListNode.id}`,
                { transform: `translateY(${-100 + 1 * textListHeightPerc}%)` },
                {
                    transform: `translateY(${-100 + 0 * textListHeightPerc}%)`,
                    ease: "none",
                    duration: scrollTime,
                },
                delayOfLastText
            )
            for (const animation of this.animationGroup) {
                if (animation.subTimelineType?.startsWith("scrollUp")) {
                    initSubTimelineTween(animation, objectId, timeline)
                }
            }

            const oldCopy = document.getElementById(objectId + "-copy")
            if (oldCopy) {
                oldCopy.remove()
            }
            const parent = textListNode.parentNode
            if (parent) {
                const textList2Node = textListNode.cloneNode(true) as HTMLElement
                textList2Node.id = objectId + "-copy"
                parent.appendChild(textList2Node)

                timeline.fromTo(
                    `#${textList2Node.id}`,
                    { transform: `translateY(${-100 + 1 * textListHeightPerc}%)` },
                    {
                        transform: `translateY(${-100 + 0 * textListHeightPerc}%)`,
                        ease: "none",
                        duration: scrollTime,
                    },
                    0
                )

                timeline.set(`#${textList2Node.id}`, {
                    transform: `translateY(${0 + 1 * textListHeightPerc}%)`,
                })
            }
        }

        timeline.set(`#${objectId}`, {
            x: `0px`,
            onComplete: () => {
                if (newTexts) {
                    // update new texts
                    this.currentJoinedTexts = newTexts
                    // parse and set texts
                    applyJoinedScrollTexts(
                        textListNode,
                        this.maskObject.childIds,
                        this.currentJoinedTexts
                    )
                }
                this.intro()
            },
        })
    }
}
